// src/polaris-components/AthleteCard.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Guardian } from "../types/athletes";
import { formatLastLoginDate } from "../utils/dateDisplay";
import AthleticOutlookActions from "./AthleticOutlookActions";
import "../styles/athlete-card.css";

export type Athlete = {
    id?: string | number;
    userId?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    lastLoginTime?: string;
    parentEmail?: string;
    parentEmail2?: string;
    location?: string;
    imageUrl?: string;
    profilePictureUrl?: string;
    staffName?: string;
    assignedStaffName?: string;
    title?: string;
    state?: string;
    stateId?: number;
    staffId?: number;
    assignedStaffId?: number;
    athleteProfileId?: number;
    athleteId?: string | number;
    sportName?: string;
    hasAthleticOutlook?: boolean;
    canEditAthleticOutlook?: boolean;
    canCreateAthleticOutlook?: boolean;
    guardians?: Guardian[];
};

type Props = {
    athlete: Athlete;
    className?: string;
    onEdit?: (athlete: Athlete) => void;
    onDelete?: (athlete: Athlete) => void;
    onPreread?: (athlete: Athlete, startInEdit: boolean) => void;
};

export default function AthleteCard({ athlete, className = "", onEdit, onDelete, onPreread }: Props) {
    const navigate = useNavigate();
    const [imageError, setImageError] = React.useState(false);
    const showActions = Boolean(onEdit || onDelete || onPreread);

    const getInitials = () => {
        const first = athlete.firstName?.charAt(0) || '';
        const last = athlete.lastName?.charAt(0) || '';
        return `${first}${last}`.toUpperCase();
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (onEdit) {
            // Ensure athlete has an ID for edit mode detection
            const athleteForEdit = {
                ...athlete,
                id: athlete.id || athlete.email // Use email as fallback ID if no ID exists
            };
            onEdit(athleteForEdit);
        }
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (onDelete) {
            onDelete(athlete);
        }
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Don't navigate if edit or delete button was clicked
        if ((e.target as HTMLElement).closest('.ath-edit-btn') ||
            (e.target as HTMLElement).closest('.ath-delete-btn') ||
            (e.target as HTMLElement).closest('.ath-preread-btn') ||
            (e.target as HTMLElement).closest('.ath-add-preread-btn')) {
            return;
        }

        // Navigate to athlete profile
        const athleteId = athlete.id || athlete.athleteProfileId;
        if (athleteId) {
            const athleteDisplayName =
                `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() || undefined;
            navigate(`/athlete/profile/${athleteId}`, {
                state: {
                    breadcrumbOrigin: 'directory',
                    ...(athleteDisplayName ? { athleteDisplayName } : {}),
                },
            });
        }
    };

    return (
        <article
            className={`ath-card-row${showActions ? "" : " ath-card-row--no-actions"} ${className}`}
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(e as any);
                }
            }}
        >
            <div className="ath-avatar">
                {(athlete.profilePictureUrl || athlete.imageUrl) && !imageError ? (
                    <img
                        src={athlete.profilePictureUrl || athlete.imageUrl}
                        alt={`${athlete.firstName} ${athlete.lastName}`}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <span className="ath-avatar-initials">{getInitials()}</span>
                )}
            </div>

            <div className="ath-col ath-col--first">
                <div className="ath-label">First</div>
                <div className="ath-val">{athlete.firstName || "—"}</div>
            </div>

            <div className="ath-col ath-col--last">
                <div className="ath-label">Last</div>
                <div className="ath-val">{athlete.lastName || "—"}</div>
            </div>

            <div className="ath-col ath-col--email">
                <div className="ath-label">Email address</div>
                <div className="ath-val">{athlete.email || "—"}</div>
            </div>

            <div className="ath-col ath-col--login">
                <div className="ath-label">Last login</div>
                <div className="ath-val">{formatLastLoginDate(athlete.lastLoginTime)}</div>
            </div>

            <div className="ath-col ath-col--staff">
                <div className="ath-label">Staff</div>
                <div className="ath-val">{athlete.assignedStaffName || athlete.staffName || "—"}</div>
            </div>

            <div className="ath-col ath-col--location">
                <div className="ath-label">Location</div>
                <div className="ath-val">{athlete.location || "—"}</div>
            </div>

            {showActions && (
                <div className="ath-col ath-col--actions ath-actions-col">
                    <div className="ath-actions">
                        {onEdit && (
                            <button className="ath-edit-btn" aria-label="Edit" onClick={handleEdit} title={`Edit ${athlete.firstName} ${athlete.lastName}`}>
                                ✎
                            </button>
                        )}
                        {onDelete && (
                            <button className="ath-delete-btn" aria-label="Delete" onClick={handleDelete} title={`Delete ${athlete.firstName} ${athlete.lastName}`}>
                                🗑
                            </button>
                        )}
                        {onPreread && (
                            <AthleticOutlookActions
                                hasAthleticOutlook={athlete.hasAthleticOutlook}
                                canCreateAthleticOutlook={athlete.canCreateAthleticOutlook}
                                athleteName={`${athlete.firstName || ""} ${athlete.lastName || ""}`.trim()}
                                onView={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onPreread(athlete, false);
                                }}
                                onCreate={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onPreread(athlete, true);
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
        </article>
    );
}
